import { useState, type FormEvent } from 'react';
import { Users, Sparkles, TrendingUp, BarChart3, Target } from 'lucide-react';
import {
  PageHeader, SectionCard, Button, Input, Badge, EmptyState, StatCard,
} from '../components/ui/light';
import { Donut, VerticalBar, RadarScore, KpiValue } from '../components/charts/ReportChart';
import {
  reportsApi,
  type ReportPayload, type PremiumReportSections,
} from '../lib/reportsApi';

type LookupState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; report: ReportPayload };

export default function VendorCreatorInsights() {
  const [url, setUrl] = useState('');
  const [state, setState] = useState<LookupState>({ status: 'idle' });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;
    setState({ status: 'loading' });
    try {
      const res = await reportsApi.generate({ profile_url: url.trim() });
      const full = await reportsApi.get(res.data.report_id);
      setState({ status: 'ok', report: full.data.report });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Lookup failed';
      setState({ status: 'error', message: msg });
    }
  }

  return (
    <div className="theme-light l-page">
      <div className="l-shell py-8">
        <PageHeader
          eyebrow="Creator Match Insights"
          title="Find creators who can sell your products"
          subtitle="Look up any creator to instantly see audience fit, monetization signals, and product-match potential."
        />

        <SectionCard variant="muted" className="mb-6">
          <form onSubmit={onSubmit} className="flex gap-3 flex-wrap">
            <div className="flex-1 min-w-64">
              <Input
                type="url"
                placeholder="Paste an Instagram, TikTok, or YouTube link"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              variant="accent"
              loading={state.status === 'loading'}
              iconLeft={<Sparkles className="w-4 h-4" />}
            >
              Run insight
            </Button>
          </form>
          {state.status === 'error' && (
            <p className="mt-3 text-sm" style={{ color: 'var(--ch2-danger)' }}>{state.message}</p>
          )}
        </SectionCard>

        {state.status === 'idle' && (
          <EmptyState
            icon={<Users className="w-6 h-6" />}
            title="No creator selected"
            description="Search for a creator above to see how their audience overlaps with your catalog."
          />
        )}

        {state.status === 'ok' && <CreatorInsightView report={state.report} />}
      </div>
    </div>
  );
}

// ── View: reuses the same building blocks the report viewer uses ──────────

function CreatorInsightView({ report }: { report: ReportPayload }) {
  const s = report.sections;
  const snap = s.snapshot;
  const aud = s.audience;
  const cohort = s.cohort;
  const monet = s.monetization_overview;
  const premium = report.variant === 'premium' ? (s as PremiumReportSections) : null;

  const genderData = [
    { name: 'Female', value: aud.gender_split.female ?? 0 },
    { name: 'Male',   value: aud.gender_split.male ?? 0 },
  ].filter(d => d.value > 0);

  const ageData = (aud.age_distribution || [])
    .map((a) => ({ name: a.label, value: a.followers_pct ?? 0 }))
    .filter(d => d.value > 0);

  const radarData = [
    { axis: 'Credibility',  value: aud.credibility_score ?? 0 },
    { axis: 'Reachability', value: aud.reachability_score ?? 0 },
    { axis: 'Engagement',   value: Math.min(100, (snap.engagement_rate ?? 0) * 1000) },
    { axis: 'Followers',    value: Math.min(100, Math.log10(Math.max(1, snap.followers ?? 1)) * 12) },
  ];

  const monetMix = (monet.mix || []).map(m => ({
    name: m.label,
    value: Math.round(m.expected),
  }));

  return (
    <>
      <div className="grid lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Followers"        value={fmtNum(snap.followers ?? 0)} hint={snap.platform || '—'} />
        <StatCard label="Engagement rate"  value={`${((snap.engagement_rate ?? 0) * 100).toFixed(2)}%`} />
        <StatCard label="Niche"            value={snap.primary_category ?? '—'} />
        <StatCard
          label="Cohort fit"
          value={cohort.income_avg ? `$${fmtNum(cohort.income_avg)}` : '—'}
          hint="peer monthly avg"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <SectionCard title="Audience gender" subtitle="Distribution">
          {genderData.length > 0
            ? <Donut data={genderData} />
            : <p className="l-muted text-sm">No gender data available.</p>}
        </SectionCard>
        <SectionCard title="Audience age" subtitle="Top age buckets">
          {ageData.length > 0
            ? <VerticalBar data={ageData} valueFormatter={(v) => `${v.toFixed(0)}%`} />
            : <p className="l-muted text-sm">No age data available.</p>}
        </SectionCard>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 mb-6">
        <div className="lg:col-span-7">
          <SectionCard title="Audience quality" subtitle="Reachability and credibility signals">
            <RadarScore data={radarData} />
          </SectionCard>
        </div>
        <div className="lg:col-span-5">
          <SectionCard title="Cohort benchmark" subtitle="Where this creator sits among peers">
            <div className="grid grid-cols-3 gap-3">
              <KpiValue value={cohort.income_low  ? `$${fmtNum(cohort.income_low)}`  : '—'} label="Low" />
              <KpiValue value={cohort.income_avg  ? `$${fmtNum(cohort.income_avg)}`  : '—'} label="Avg" />
              <KpiValue value={cohort.income_high ? `$${fmtNum(cohort.income_high)}` : '—'} label="High" />
            </div>
            <p className="l-muted text-sm mt-4">
              {cohort.cohort_label} · {cohort.sample_size.toLocaleString()} comparable creators
            </p>
          </SectionCard>
        </div>
      </div>

      <SectionCard
        title="Monetization mix"
        subtitle="Expected monthly revenue by category"
        actions={<Badge variant="accent"><TrendingUp className="w-3 h-3 inline mr-1" />Estimate</Badge>}
        className="mb-6"
      >
        {monetMix.length > 0
          ? <Donut data={monetMix} />
          : <p className="l-muted text-sm">No monetization breakdown available.</p>}
      </SectionCard>

      {premium && premium.product_matches && premium.product_matches.length > 0 && (
        <SectionCard
          title="Top product matches"
          subtitle="From your catalog"
          actions={<Badge variant="accent"><Target className="w-3 h-3 inline mr-1" />Premium</Badge>}
        >
          <div className="grid md:grid-cols-2 gap-3">
            {premium.product_matches.slice(0, 6).map((p) => (
              <div key={p.product_id} className="l-card-flat p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate" style={{ color: 'var(--ch2-ink)' }}>{p.title}</div>
                    <div className="l-subtle text-xs">{p.vertical ?? '—'}</div>
                  </div>
                  <Badge variant="accent">{(p.score * 100).toFixed(0)}</Badge>
                </div>
                {p.why && <p className="text-sm l-muted mt-2">{p.why}</p>}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {report.variant === 'free' && (
        <div className="l-card-muted p-5 text-sm flex items-center gap-3">
          <BarChart3 className="w-5 h-5 shrink-0" style={{ color: 'var(--ch2-accent-2)' }} />
          <span style={{ color: 'var(--ch2-ink-2)' }}>
            Upgrade to unlock product matches, vendor matches, and the full action plan for this creator.
          </span>
        </div>
      )}
    </>
  );
}

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${Math.round(n)}`;
}
